import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCreatePackageOrder, usePackageDetails, useUserSelfDetails } from '@steelbuy/data-access';
import { useAddressCollectionData } from '@steelbuy/data-provider';
import { useModal } from '@steelbuy/modal-dialog';
import { ModelPrimaryKey, formatCurrency, AddressModel } from '@steelbuy/ts-shared';
import { CheckoutCartSummary, ListingBuyerTeaser } from '@steelbuy/ui-domain';
import {
    Accordion,
    AddressIdentifier,
    ButtonCallToAction,
    ButtonSecondary,
    Card,
    CardContentCheckoutPayment,
    CardContentProductViewPrice,
    CardLayout,
    FormActionbar,
    LoadingSpinner,
    LoadingStatus,
    NotificationLevel,
    Notification,
} from '@steelbuy/ui-primitive';
import { useWindowSize } from '@steelbuy/util';
import { CheckoutCancelModal } from './CheckoutCancelModal';
import CheckoutCheckboxes from './CheckoutCheckboxes';
import { useCheckoutContext } from './CheckoutContext';
import { CheckoutErrorNotification } from './CheckoutErrorNotification';
import CheckoutPurchaseOrderNumber from './CheckoutPurchaseOrderNumber';
import { CHOOSE_ADDRESS_RELATIVE_PATH } from '../../router/Routes';
import { TableLayout } from '../../views/layouts/table-layout/TableLayout';
import { AddressDetails } from '../address-details/AddressDetails';
import { CheckoutPackageError } from '../checkout-error/CheckoutPackageError';
import { CheckoutSuccessPackage } from '../checkout-success/CheckoutSuccessPackage';
import { PageHeader } from '../page-header/PageHeader';

interface CheckoutPackageFormProps {
    packageDetailPath: string;
    packageListingId: ModelPrimaryKey;
}

const CheckoutPackageForm = ({ packageDetailPath, packageListingId }: CheckoutPackageFormProps) => {
    const { t } = useTranslation(['translation', 'domainModel', 'uiDomain']);
    const navigate = useNavigate();
    const { width } = useWindowSize();
    const SHOW_LARGE_SCREEN_SIZE = 1327;

    const checkoutContext = useCheckoutContext();
    const { mutate, isError, isLoading, reset } = useCreatePackageOrder();
    const { invoiceAddressCollectionData, warehouseAddressCollectionData } = useAddressCollectionData();
    const { data: packageModel, refetch, isRefetching } = usePackageDetails({ packageId: packageListingId });
    const { data: userData } = useUserSelfDetails();
    const cancelModal = useModal();
    const [showSuccess, setShowSuccess] = useState(false);
    const [orderPlaced, setOrderPlaced] = useState<string | null>(null);
    const [retryCount, setRetryCount] = useState(0);
    const [showPriceUpdateError, setShowPriceUpdateError] = useState(false);
    const invoiceAddresses = invoiceAddressCollectionData.query();
    const warehouseAddresses = warehouseAddressCollectionData.query();

    const defaultInvoiceAddressId = invoiceAddresses[0]?.id ?? '';
    const defaultWarehouseAddressId =
        (warehouseAddresses.find((address: AddressModel) => address.primary) ?? warehouseAddresses[0])?.id ?? '';

    const checkoutPrice = useRef<number | null>(null);
    const {
        formState: { isValid },
        control,
        trigger,
    } = useForm({ mode: 'all' });

    const onRetry = () => {
        setTimeout(() => {
            setRetryCount((prev) => prev + 1);
        }, 0);
    };

    useEffect(() => {
        if (!checkoutContext.deliveryAddressId) {
            checkoutContext.setDeliveryAddressId(defaultWarehouseAddressId);
        }
    }, [defaultWarehouseAddressId]);

    const createPackagePurchaseOrder = () => {
        if (checkoutPrice.current === packageModel?.pricing.priceIncludingDelivery.value) {
            mutate(
                {
                    packagingId: packageListingId,
                    invoiceAddressId: defaultInvoiceAddressId,
                    deliveryAddressId: checkoutContext.deliveryAddressId,
                    additionalDeliveryInformation: checkoutContext.additionalDeliveryInformation,
                    buyerOrderNumber: checkoutContext.buyerOrderNumber?.trim(),
                    haulierRequired: checkoutContext.haulierRequired,
                },
                {
                    onSuccess: ({ orderNumber }) => {
                        setShowSuccess(true);
                        setOrderPlaced(orderNumber);
                    },
                }
            );
        } else {
            checkoutContext.setAgreedAuthorised(false);
            checkoutContext.setAgreedTermsAndConditions(false);
            setShowPriceUpdateError(true);
        }
    };

    useEffect(() => {
        if (checkoutPrice.current !== null && !isRefetching && packageModel) {
            createPackagePurchaseOrder();
        }
    }, [isRefetching]);

    useEffect(() => {
        if (!isValid && (checkoutContext.agreedTermsAndConditions || checkoutContext.agreedAuthorised)) {
            trigger();
        }
    }, [checkoutContext.agreedTermsAndConditions, checkoutContext.agreedAuthorised]);

    if (!packageModel) {
        return <LoadingSpinner />;
    }
    let errorMessage = '';
    if (
        !checkoutContext.agreedTermsAndConditions ||
        !checkoutContext.agreedAuthorised ||
        !isValid ||
        checkoutContext.haulierRequired === undefined
    ) {
        errorMessage = t('translation:application.checkout.incompleteInformationNotification');
    }
    const buyIsDisabled = () => {
        if (!checkoutContext.agreedTermsAndConditions || !isValid || checkoutContext.haulierRequired === undefined) {
            return true;
        }
        return !checkoutContext.agreedAuthorised;
    };
    const handleCheckout = () => {
        if (!checkoutContext.deliveryAddressId) {
            return;
        }
        checkoutPrice.current = packageModel.pricing.priceIncludingDelivery.value;
        refetch();
    };

    if (showSuccess && orderPlaced) return <CheckoutSuccessPackage orderModel={{ orderNumber: orderPlaced }} />;

    if (isError)
        return (
            <CheckoutPackageError
                packageModel={packageModel}
                onRetry={onRetry}
                retryCount={retryCount}
                onBack={() => {
                    setRetryCount(0);
                    reset();
                }}
            />
        );
    return (
        <div className="checkout">
            <PageHeader
                pageTitle={t('translation:application.checkout.header.title')}
                previousPageTitle={t('uiDomain:common.back')}
                onBackClick={() => {
                    checkoutContext.setBuyerOrderNumber('');
                    navigate(packageDetailPath);
                }}
                showCartIcon={false}
            />
            <TableLayout>
                {showPriceUpdateError && (
                    <Notification
                        level={NotificationLevel.WARNING}
                        message={t('translation:application.checkout.priceUpdatedError')}
                        className="price-updated-error"
                        disableScroll={false}
                    />
                )}
            </TableLayout>
            <div className="checkout-view">
                <div className="checkout-view-details">
                    <Card isClickable={false}>
                        <CardLayout>
                            <AddressDetails
                                addressId={defaultInvoiceAddressId}
                                type={AddressIdentifier.BILLING_ADDRESS}
                                additionalInfoLabel={t(
                                    'translation:application.addressDetails.additionalInformationLabel'
                                )}
                            />
                        </CardLayout>
                    </Card>

                    <CheckoutPurchaseOrderNumber
                        setBuyerOrderNumber={checkoutContext.setBuyerOrderNumber}
                        control={control}
                        defaultValue={checkoutContext.buyerOrderNumber}
                    />

                    {/* checkout package payment  */}
                    <Card isClickable={false}>
                        <CardLayout>
                            <CardContentCheckoutPayment
                                header={t('translation:application.checkout.payment.sectionHeading')}
                                notificationMessage={t(
                                    'translation:application.checkout.payment.creditInfoNotification'
                                )}
                            />
                        </CardLayout>
                    </Card>

                    <Card isClickable={false}>
                        <CardLayout>
                            <AddressDetails
                                addressId={checkoutContext.deliveryAddressId}
                                type={AddressIdentifier.DELIVERY_ADDRESS}
                                onButtonClick={() => navigate(CHOOSE_ADDRESS_RELATIVE_PATH)}
                                additionalText={checkoutContext.additionalDeliveryInformation ?? ''}
                                onChangeAdditionalText={(value) =>
                                    checkoutContext.setAdditionalDeliveryInformation(value)
                                }
                                additionalInfoLabel={t(
                                    'translation:application.addressDetails.deliveryInformationLabel'
                                )}
                                additionalInfoPlaceholder={t(
                                    'translation:application.pickupAddressStep.additionalInfoPlaceholder'
                                )}
                                isCartCheckout
                                isMandatory
                            />
                        </CardLayout>
                    </Card>

                    <Card isClickable={false}>
                        <ListingBuyerTeaser
                            listing={packageModel}
                            isPackage
                            isCheckout
                            isCartCheckout
                            checkoutContext={checkoutContext}
                            isCart
                        />
                    </Card>
                    {/* Check boxes */}
                    <div className="input-checkbox-container">{userData ? <CheckoutCheckboxes /> : null}</div>

                    {errorMessage && <CheckoutErrorNotification errorMessage={errorMessage} />}
                    <FormActionbar>
                        <ButtonSecondary label={t('uiDomain:common.cancel')} onClick={cancelModal.show} />
                        <ButtonCallToAction
                            label={t('translation:application.checkout.submitCheckoutButtonLabel')}
                            disabled={buyIsDisabled() || isLoading}
                            onClick={() => handleCheckout()}
                            loadingStatus={isLoading ? LoadingStatus.PENDING : LoadingStatus.IDLE}
                        />
                    </FormActionbar>

                    <CheckoutCancelModal
                        modalAccessor={cancelModal}
                        action={() => {
                            checkoutContext.reset();
                            navigate(packageDetailPath);
                        }}
                    />
                </div>
                <div className="checkout-view-summary">
                    <div className="checkout-view-summary__list">
                        <Card isClickable={false}>
                            <CardLayout>
                                {width > SHOW_LARGE_SCREEN_SIZE ? (
                                    <div className="checkout-view-summary__list__withoutAccordion">
                                        <div className="checkout-view-summary__list__items">
                                            {t('uiDomain:checkoutCart.item', {
                                                count: 1,
                                            })}
                                        </div>
                                        <div>
                                            <CheckoutCartSummary
                                                cartData={{
                                                    id: packageModel.id,
                                                    status: '',
                                                    createdAt: {
                                                        discriminator: 'Timestamp',
                                                        value: '',
                                                    },
                                                    product: {
                                                        ...packageModel,
                                                    },
                                                    productType: 'PACKAGE',
                                                }}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="checkout-view-summary__list__withAccordion">
                                        <Accordion
                                            collapsedLabel={t('uiDomain:checkoutCart.item', {
                                                count: 1,
                                            })}
                                            extendedLabel={t('uiDomain:checkoutCart.item', {
                                                count: 1,
                                            })}
                                        >
                                            <div>
                                                <CheckoutCartSummary
                                                    cartData={{
                                                        id: packageModel.id,
                                                        status: '',
                                                        createdAt: {
                                                            discriminator: 'Timestamp',
                                                            value: '',
                                                        },
                                                        product: { ...packageModel },
                                                        productType: 'PACKAGE',
                                                    }}
                                                />
                                            </div>
                                        </Accordion>
                                    </div>
                                )}
                            </CardLayout>
                        </Card>
                    </div>
                    <div className="checkout-view-summary__price">
                        <Card isClickable={false}>
                            <CardLayout>
                                <CardContentProductViewPrice
                                    header={t('translation:application.checkoutDeliveryOption.orderSummary')}
                                    pricePerUnit=""
                                    pricePerUnitLabel={t('translation:application.checkoutDeliveryOption.totalWeight')}
                                    isCartCheckout
                                    weight=""
                                    typeWithWeight={[
                                        {
                                            type: packageModel.material,
                                            weight: packageModel.weight,
                                            tradeUnit: packageModel.tradeUnit,
                                        },
                                    ]}
                                    totalExVat={
                                        formatCurrency(
                                            packageModel.pricing.totalExcludingVat.value,
                                            packageModel.pricing.currencyCode
                                        ) ?? ''
                                    }
                                    vatPercent={(packageModel.pricing.taxPercent / 100).toString() ?? '0'}
                                    totalPrice={
                                        formatCurrency(
                                            packageModel.pricing.totalIncludingVat.value,
                                            packageModel.pricing.currencyCode
                                        ) ?? ''
                                    }
                                    vat={
                                        formatCurrency(
                                            packageModel.pricing.vat.value,
                                            packageModel.pricing.currencyCode
                                        ) ?? ''
                                    }
                                />
                            </CardLayout>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckoutPackageForm;
